import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import PlaceAutocomplete from '@/components/PlaceAutocomplete';
import { Lighthouse, MapPin, Clock, Sparkle, Users, Plus, X } from '@phosphor-icons/react';

const KIND_PALETTE = {
  meetup:      { label: 'Meetup',      tint: 'text-bx-cyan',     bg: 'from-bx-cyan/20 to-transparent' },
  convergence: { label: 'Convergence', tint: 'text-bx-volt',     bg: 'from-bx-volt/20 to-transparent' },
  celebration: { label: 'Celebration', tint: 'text-bx-gold',     bg: 'from-bx-gold/20 to-transparent' },
  vigil:       { label: 'Vigil',       tint: 'text-bx-magenta',  bg: 'from-bx-magenta/20 to-transparent' },
  night:       { label: 'Night',       tint: 'text-bx-legendary',bg: 'from-bx-legendary/20 to-transparent' },
};

const LighthouseCard = ({ lh }) => {
  const k = KIND_PALETTE[lh.kind] || KIND_PALETTE.meetup;
  const photoUrl = lh.place_photo_ref
    ? `${api.defaults.baseURL}/places/photo?ref=${encodeURIComponent(lh.place_photo_ref)}&max_width=800`
    : null;
  const when = new Date(lh.scheduled_at);
  return (
    <Link
      to={`/lighthouse/${lh.id}`}
      data-testid={`lighthouse-card-${lh.id}`}
      className={`relative glass rounded-3xl overflow-hidden group hover:scale-[1.01] transition`}>
      {photoUrl ? (
        <div className="aspect-[16/9] relative">
          <img src={photoUrl} alt={lh.place_name} className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-75 transition" />
          <div className={`absolute inset-0 bg-gradient-to-t ${k.bg} to-black/80`} />
        </div>
      ) : (
        <div className={`aspect-[16/9] bg-gradient-to-br ${k.bg} to-black`} />
      )}
      <div className="absolute inset-0 p-5 flex flex-col justify-end">
        <div className={`text-[10px] uppercase tracking-[0.4em] ${k.tint} mb-1 flex items-center gap-1`}>
          <Lighthouse size={12} weight="fill" /> {k.label}
          {lh.is_adult && <span className="ml-2 px-2 py-0.5 rounded-full bg-bx-magenta/30 text-bx-magenta text-[9px]">21+</span>}
        </div>
        <div className="font-display text-xl sm:text-2xl font-black leading-tight">{lh.place_name}</div>
        <div className="text-xs text-white/60 mt-1 flex items-center gap-1">
          <MapPin size={11} weight="duotone" /> {lh.place_address}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-white/70">
          <span className="flex items-center gap-1"><Clock size={12} weight="duotone" /> {when.toLocaleString([], {weekday:'short', hour:'numeric', minute:'2-digit'})}</span>
          <span className="flex items-center gap-1"><Users size={12} weight="duotone" /> {(lh.rsvps||[]).length} drifting in</span>
          {lh.pool_resonance > 0 && (
            <span className="flex items-center gap-1 text-bx-volt"><Sparkle size={12} weight="fill" /> {lh.pool_resonance} ✦ pool</span>
          )}
        </div>
        <div className="mt-2 text-xs text-white/55 italic line-clamp-2">{lh.blurb}</div>
      </div>
    </Link>
  );
};

const Lighthouses = () => {
  const { user } = useAuth();
  const [lighthouses, setLighthouses] = useState([]);
  const [bubbles, setBubbles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForge, setShowForge] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/lighthouses/active'),
        api.get('/bubbles'),
      ]);
      setLighthouses(a.data.lighthouses || []);
      setBubbles(b.data.bubbles || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10 pb-32" data-testid="lighthouses-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan">the world layer</div>
          <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">Lighthouses</h1>
          <p className="text-white/60 mt-3 text-sm sm:text-base max-w-2xl">
            Pin a real place. Schedule when it should glow. Drop ✦ in the pool. The first bubbles to physically show up share the warmth.
          </p>
        </div>
        <button
          onClick={() => setShowForge(true)}
          data-testid="forge-lighthouse-btn"
          className="btn-volt px-5 py-3 rounded-full text-sm font-bold flex items-center gap-2 self-start">
          <Plus size={16} weight="bold" /> Forge a Lighthouse
        </button>
      </div>

      {loading ? (
        <div className="text-white/60">listening to the world…</div>
      ) : lighthouses.length === 0 ? (
        <div className="glass rounded-3xl p-10 text-center">
          <Lighthouse size={32} className="text-bx-cyan mx-auto mb-3" weight="duotone" />
          <div className="text-white/65 mb-3">The world is dark. Be the first to light one.</div>
          <button onClick={() => setShowForge(true)} className="btn-volt px-5 py-2 rounded-full text-xs font-bold">
            Forge a Lighthouse
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {lighthouses.map((lh) => (
            <LighthouseCard key={lh.id} lh={lh} />
          ))}
        </div>
      )}

      {showForge && (
        <ForgeModal
          bubbles={bubbles}
          isAdultUser={!!user?.is_age_verified}
          onClose={() => setShowForge(false)}
          onForged={() => { setShowForge(false); load(); }}
        />
      )}
    </div>
  );
};

const KIND_OPTIONS = [
  { id: 'meetup',      label: 'Meetup' },
  { id: 'convergence', label: 'Convergence' },
  { id: 'celebration', label: 'Celebration' },
  { id: 'vigil',       label: 'Vigil' },
  { id: 'night',       label: 'Night (21+)' },
];

const ForgeModal = ({ bubbles, isAdultUser, onClose, onForged }) => {
  const [place, setPlace] = useState(null);
  const [bubbleId, setBubbleId] = useState(bubbles[0]?.id || '');
  const [kind, setKind] = useState('meetup');
  const [when, setWhen] = useState(() => {
    const d = new Date(Date.now() + 90 * 60 * 1000);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [windowMin, setWindowMin] = useState(120);
  const [blurb, setBlurb] = useState('');
  const [poolN, setPoolN] = useState(5);
  const [busy, setBusy] = useState(false);
  const isAdult = kind === 'night';

  const submit = async () => {
    if (!place) { toast.error('pick a venue first'); return; }
    if (!bubbleId) { toast.error('pick a bubble'); return; }
    if (isAdult && !isAdultUser) { toast.error('21+ verification required for Night lighthouses'); return; }
    setBusy(true);
    try {
      await api.post('/lighthouses', {
        bubble_id: bubbleId,
        place_id: place.place_id,
        kind,
        scheduled_at: new Date(when).toISOString(),
        window_minutes: Number(windowMin),
        blurb: blurb.slice(0, 240),
        pool_first_n: Number(poolN),
        is_adult: isAdult,
      });
      toast.success(`${place.name} will glow at the appointed time ✦`);
      onForged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'could not forge');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[300] bg-black/85 backdrop-blur grid place-items-end sm:place-items-center px-4" onClick={onClose}>
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-t-3xl sm:rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-bx-cyan">forge a lighthouse</div>
            <div className="font-display text-2xl font-black mt-1">Pick a real place.</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 text-white/60"><X size={16} weight="bold" /></button>
        </div>

        <div className="space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">venue</div>
            <PlaceAutocomplete onPlaceSelected={setPlace} placeholder="Encore Bar, Roberta's, Central Park..." />
            {place && (
              <div className="mt-2 glass-hi rounded-xl p-3 text-sm">
                <div className="font-bold">{place.name}</div>
                <div className="text-xs text-white/55">{place.formatted_address}</div>
                {place.rating ? (
                  <div className="text-[11px] text-bx-gold mt-1">★ {place.rating} · {place.user_ratings_total} ratings</div>
                ) : null}
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">your bubble carries this</div>
            <select
              value={bubbleId}
              onChange={(e) => setBubbleId(e.target.value)}
              data-testid="forge-bubble-select"
              className="w-full glass-hi rounded-xl p-3 text-sm bg-transparent outline-none">
              {bubbles.map((b) => <option key={b.id} value={b.id} className="bg-black">{b.name} · L{b.level || 1}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">kind</div>
              <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="w-full glass-hi rounded-xl p-3 text-sm bg-transparent outline-none"
                data-testid="forge-kind">
                {KIND_OPTIONS.map((k) => <option key={k.id} value={k.id} className="bg-black">{k.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">window (min)</div>
              <input type="number" min={15} max={480} value={windowMin}
                onChange={(e) => setWindowMin(e.target.value)}
                className="w-full glass-hi rounded-xl p-3 text-sm outline-none" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">when</div>
              <input type="datetime-local" value={when}
                onChange={(e) => setWhen(e.target.value)}
                data-testid="forge-when"
                className="w-full glass-hi rounded-xl p-3 text-sm outline-none" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">pool splits to first N</div>
              <input type="number" min={1} max={25} value={poolN}
                onChange={(e) => setPoolN(e.target.value)}
                className="w-full glass-hi rounded-xl p-3 text-sm outline-none" />
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 mb-2">blurb</div>
            <textarea value={blurb} maxLength={240} rows={2}
              placeholder="first round on me · or anything you want bubbles to know"
              onChange={(e) => setBlurb(e.target.value)}
              data-testid="forge-blurb"
              className="w-full glass-hi rounded-xl p-3 text-sm outline-none placeholder-white/30" />
          </div>

          <button
            onClick={submit}
            disabled={busy || !place}
            data-testid="forge-submit"
            className="w-full btn-volt px-5 py-3 rounded-full text-sm font-bold disabled:opacity-50">
            {busy ? 'lighting…' : 'Light the Lighthouse'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Lighthouses;
